import './productGroup.scss';

import { Fragment, ReactNode, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Icon, Skeleton } from '@eliberty/design-system-react';

import { ClockIcon, FormattedCurrency } from '~atoms';
import { CartParticipantLine } from '~molecules';
import type { ProductDisplay } from '~types/cart';

export interface ProductGroupProps {
  product: ProductDisplay;
  isPriceLoading?: boolean;
  onRemoveParticipant: (productId: string, participantId: number) => void;
  onRemoveProduct: (productId: string) => void;
}

const TrashIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='18'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='1.8'
    viewBox='0 0 24 24'
    width='18'
  >
    <path d='M4 7h16M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12M10 11v6M14 11v6' />
  </svg>
);

const ProductGroup = ({
  isPriceLoading = false,
  onRemoveProduct,
  onRemoveParticipant,
  product,
}: ProductGroupProps) => {
  const intl = useIntl();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const count = product.participants.length;

  const participantSummary = useMemo(() => {
    const counts = new Map<string, number>();
    product.participants.forEach((participant) => {
      counts.set(
        participant.consumerCategoryLabel,
        (counts.get(participant.consumerCategoryLabel) ?? 0) + 1,
      );
    });
    return Array.from(counts.entries())
      .map(([label, n]) => `${n} ${label}`)
      .join(' · ');
  }, [product.participants]);

  const metaItems = [
    product.skiDate && {
      id: 'date',
      node: (
        <FormattedDate value={product.skiDate} day='numeric' month='long' year='numeric' />
      ),
    },
    product.timeslotLabel && {
      id: 'timeslot',
      node: (
        <span className='ProductGroup_timeslotPill'>
          <ClockIcon />
          {product.timeslotLabel}
        </span>
      ),
    },
    {
      id: 'count',
      node: (
        <FormattedMessage
          defaultMessage='{count, plural, one {# participant} other {# participants}}'
          id='cartParticipantCount'
          values={{ count }}
        />
      ),
    },
    product.optionCount > 0 && {
      id: 'options',
      node: (
        <FormattedMessage
          defaultMessage='{count, plural, one {# option} other {# options}}'
          id='cartOptionCount'
          values={{ count: product.optionCount }}
        />
      ),
    },
  ].filter(Boolean) as { id: string; node: ReactNode }[];

  return (
    <div className={`ProductGroup${isCollapsed ? ' ProductGroup-collapsed' : ''}`}>
      <div className='ProductGroup_header'>
        <button
          aria-expanded={!isCollapsed}
          aria-label={product.validityCategoryLabel || product.productCategoryName}
          className='ProductGroup_toggle'
          onClick={() => setIsCollapsed((c) => !c)}
          type='button'
        >
          <span className='ProductGroup_heading'>
            <span className='ProductGroup_category'>{product.productCategoryName}</span>
            <span className='ProductGroup_title'>{product.validityCategoryLabel}</span>
            <span className='ProductGroup_meta'>
              {metaItems.map((meta, index) => (
                <Fragment key={meta.id}>
                  {index > 0 && <span aria-hidden className='ProductGroup_dot' />}
                  {meta.node}
                </Fragment>
              ))}
            </span>
          </span>
          <span aria-hidden className='ProductGroup_chevron'>
            <Icon name='chevron-down' />
          </span>
        </button>
        <button
          aria-label={intl.formatMessage({
            defaultMessage: 'Remove this product',
            id: 'cartAriaRemoveProduct',
          })}
          className='ProductGroup_remove'
          onClick={() => onRemoveProduct(product.productId)}
          type='button'
        >
          <TrashIcon />
        </button>
      </div>
      {!isCollapsed && (
        <div className='ProductGroup_participants'>
          {product.participants.map((participant) => (
            <CartParticipantLine
              currency={product.currency}
              isPriceLoading={isPriceLoading}
              key={participant.participantId}
              onRemove={() => onRemoveParticipant(product.productId, participant.participantId)}
              participant={participant}
            />
          ))}
        </div>
      )}
      <div className='ProductGroup_footer'>
        <span className='ProductGroup_subTotalLabel'>
          {isCollapsed ? (
            participantSummary
          ) : (
            <FormattedMessage defaultMessage='Subtotal' id='cartSubTotal' />
          )}
        </span>
        <span className='ProductGroup_subTotalValue'>
          {isPriceLoading ? (
            <Skeleton height={20} width={72} />
          ) : (
            <FormattedCurrency currency={product.currency} value={product.totalInCents} />
          )}
        </span>
      </div>
    </div>
  );
};

export default ProductGroup;
